import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState,
} from 'react';
import { carousel as c } from '../../component-classes';
import useThrottled from '../hooks/useThrottled';

export type ScrollState = {
    scrollLeft: number;
    offsetWidth: number;
    scrollWidth: number;
    scrollStart: boolean;
    scrollEnd: boolean;
    noScroll: boolean;
};

type CarouselProps = {
    as?: string;
    bleed?: boolean;
    children?: React.ReactNode;
    className?: string;
    contain?: boolean;
    disableScroll?: boolean;
    onScroll?: (scrollState: ScrollState) => void;
    scrollLeft?: number;
    scrollbar?: boolean;
    smooth?: boolean;
    snap?: boolean;
};

export type CarouselHandle = {
    scrollState: ScrollState;
    scrollBy: (left: number) => void;
    scrollTo: (left: number) => void;
};

const SCROLL_PADDING_PX = 16;
const SCROLL_THROTTLE_TIMEOUT_MS = 100;

function getAdjustedScrollState(
    bleed: boolean,
    listElement?: HTMLUListElement,
): ScrollState {
    const scrollLeft = listElement?.scrollLeft ?? 0;
    const offsetWidth =
        (listElement?.offsetWidth ?? 0) - (bleed ? 2 * SCROLL_PADDING_PX : 0);
    const scrollWidth =
        (listElement?.scrollWidth ?? 0) - (bleed ? 2 * SCROLL_PADDING_PX : 0);
    const isStartScroll = scrollLeft === 0;
    const isEndScroll = scrollLeft + offsetWidth === scrollWidth;
    const hasNoScroll = offsetWidth === scrollWidth;
    return {
        scrollLeft,
        offsetWidth,
        scrollWidth,
        scrollStart: isStartScroll,
        scrollEnd: isEndScroll,
        noScroll: hasNoScroll,
    };
}

export const Carousel = forwardRef(
    (props: CarouselProps, ref: React.Ref<CarouselHandle>) => {
        const {
            as = 'ul',
            bleed = false,
            children,
            className = '',
            contain = false,
            disableScroll = false,
            onScroll,
            scrollLeft = 0,
            scrollbar = false,
            smooth = false,
            snap = false,
        } = props;

        const [listElement, setListElement] = useState<HTMLUListElement>();

        const callbackRef = useCallback((callbackElement: HTMLUListElement) => {
            if (callbackElement !== null) {
                setListElement(callbackElement);
            }
        }, []);

        useEffect(() => {
            listElement?.scrollTo({
                left: scrollLeft,
            });
        }, [listElement, scrollLeft]);

        // TODO: check if imperativeHandle needs listElement (and bleed) as dep
        useImperativeHandle(ref, () => ({
            scrollState: getAdjustedScrollState(bleed, listElement),
            scrollBy(left: number) {
                listElement?.scrollBy({
                    left,
                });
            },
            scrollTo(left: number) {
                listElement?.scrollTo({
                    left,
                });
            },
        }));

        const checkScrollPosition = useCallback(() => {
            if (!listElement) {
                return;
            }
            if (onScroll) {
                const scrollState = getAdjustedScrollState(bleed, listElement);
                onScroll(scrollState);
            }
        }, [bleed, listElement, onScroll]);

        useEffect(() => {
            checkScrollPosition();
        }, [checkScrollPosition]);

        const throttledCheckScrollPosition = useThrottled(
            checkScrollPosition,
            SCROLL_THROTTLE_TIMEOUT_MS,
        );

        const carouselClassName = `${disableScroll ? c.noScrollList : c.list} ${
            as === 'ul' ? 'mb-0' : ''
        } ${bleed ? c.bleed : ''} ${contain ? c.contain : ''} ${
            scrollbar ? 'pb-16' : c.scrollbarHidden
        } ${smooth ? c.smoothScroll : ''} ${
            snap ? c.scrollSnap : ''
        } ${className}`;

        const carouselStyle: React.CSSProperties = {};
        if (snap && bleed) {
            carouselStyle.scrollPadding = `${SCROLL_PADDING_PX}px`;
        }

        return React.createElement(
            as,
            {
                ref: callbackRef,
                className: carouselClassName,
                onScroll: throttledCheckScrollPosition,
                style: carouselStyle,
            },
            children,
        );
    },
);

type CarouselItemProps = {
    children?: React.ReactNode;
    className?: string;
};

export function CarouselItem(props: CarouselItemProps) {
    const { children, className } = props;

    return <li className={className}>{children}</li>;
}
